import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { TourPartyMemberAvatar } from './TourPartyMemberAvatar';
import { TourPartyMemberCardShell } from './TourPartyMemberCardShell';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import { INVITE_STATUS_META, resolveMemberLabel } from '../../utils/tourPartyMemberUtils';

export function TourPartyMemberCard({
  member,
  theme,
  isDarkMode,
  compact = false,
  trailing = null,
  onPress,
}) {
  const { isDarkMode: ctxDark } = useTheme();
  const dark = isDarkMode ?? ctxDark;
  const glassStyles = useCalendarGlassStyles(theme, dark);
  const status = String(member?.status || 'accepted').toLowerCase();
  const statusMeta = INVITE_STATUS_META[status] || INVITE_STATUS_META.pending;
  const displayName = resolveMemberLabel(member);

  return (
    <TourPartyMemberCardShell
      isDarkMode={dark}
      onPress={onPress}
      style={[localStyles.shell, { overflow: 'hidden' }]}
    >
      <View style={[localStyles.body, compact && localStyles.bodyCompact]}>
        <View style={localStyles.row}>
          <TourPartyMemberAvatar member={member} size={compact ? 36 : 44} theme={theme} />
          <View style={localStyles.info}>
            <Text style={[localStyles.name, { color: theme.text.primary }]} numberOfLines={1}>
              {displayName}
            </Text>
            {member.username ? (
              <Text style={[localStyles.username, { color: theme.brand.accent }]} numberOfLines={1}>
                @{member.username}
              </Text>
            ) : null}
            {!compact && member.email ? (
              <Text style={[localStyles.email, { color: theme.text.secondary }]} numberOfLines={1}>
                {member.email}
              </Text>
            ) : null}
            <View style={localStyles.metaRow}>
              {member.userTypeLabel ? (
                <View style={[glassStyles.typePill, { borderColor: member.userTypeColor || theme.brand.accent }]}>
                  <Text style={[glassStyles.typePillText, { color: member.userTypeColor || theme.brand.accent }]}>
                    {member.userTypeLabel}
                  </Text>
                </View>
              ) : null}
              {member.inviteId ? (
                <View style={[glassStyles.statusPill, { borderColor: statusMeta.color }]}>
                  <Ionicons name={statusMeta.icon} size={11} color={statusMeta.color} />
                  <Text style={[glassStyles.statusText, { color: statusMeta.color }]}>{statusMeta.label}</Text>
                </View>
              ) : member.role ? (
                <View style={[glassStyles.statusPill, { borderColor: theme.brand.primary }]}>
                  <Text style={[glassStyles.statusText, { color: theme.brand.primary }]}>{member.role}</Text>
                </View>
              ) : null}
            </View>
          </View>
          {trailing}
        </View>
      </View>
    </TourPartyMemberCardShell>
  );
}

const localStyles = StyleSheet.create({
  shell: { marginTop: 4 },
  body: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    position: 'relative',
    zIndex: 2,
  },
  bodyCompact: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  info: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  name: {
    fontSize: 14,
    fontWeight: '800',
  },
  username: {
    fontSize: 11,
    fontWeight: '600',
  },
  email: {
    fontSize: 11,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
});

export default TourPartyMemberCard;
